// ==================== FILE: src/config/restaurantConfig.js ====================

import { brandingService } from './brandingService';
import { TEMPLATES } from './TemplateStyles';
import { buildSectionsFromTemplate, buildNavLinksFromSections } from './SectionRegistry';

// Helper to clean and decode HTML content from the API
const cleanHtmlContent = (html) => {
  if (!html) return '';

  // 1. Decode HTML entities
  let decoded = html;
  try {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    decoded = txt.value;
  } catch (e) {
    // Fallback if document is unavailable
  }

  // 2. Completely strip out any inline CSS styles injected by the admin editor
  // This guarantees that only the website's CSS is used.
  decoded = decoded.replace(/\s*style="[^"]*"/gi, '');
  decoded = decoded.replace(/\s*style='[^']*'/gi, '');
  decoded = decoded.replace(/<([a-zA-Z0-9]+)style=[^\s>]+> ?/gi, '<$1>'); // Fallback for mangled styles

  // 4. Fix invalid <br>...</br> usage (br is self-closing, </br> is invalid)
  decoded = decoded.replace(/<\/br>/gi, '');

  // 5. Remove duplicate consecutive paragraphs (exact duplicates)
  decoded = decoded.replace(/(<p[^>]*>[\s\S]*?<\/p>)\s*\1/gi, '$1');

  // 5.a Replace all forms of non-breaking spaces with a single normal space
  decoded = decoded.replace(/&nbsp;/gi, ' ');
  decoded = decoded.replace(/\u00A0/g, ' ');

  // Collapse multiple normal spaces into one space to fix mid-sentence gaps
  decoded = decoded.replace(/ {2,}/g, ' ');

  // 5.b Remove empty paragraphs and divs that cause huge vertical gaps
  decoded = decoded.replace(/<p[^>]*>(\s|<br\s*\/?>)*<\/p>/gi, '');
  decoded = decoded.replace(/<div[^>]*>(\s|<br\s*\/?>)*<\/div>/gi, '');

  // 5.c Replace multiple consecutive <br> tags with a single <br>
  decoded = decoded.replace(/(<br\s*\/?>\s*){2,}/gi, '<br />');

  // 6. Strip <pre> and <code> tags that the admin editor might wrap the HTML in
  decoded = decoded.replace(/<\/?pre[^>]*>/gi, '');
  decoded = decoded.replace(/<\/?code[^>]*>/gi, '');

  return decoded;
};

const _defaultTemplateSections = TEMPLATES.TEMPLATE15.components.defaultSections || [];

/**
 * Structural skeleton for initial state.
 */
const skeletonConfig = {
  id: "0",
  name: "Loading...",
  outletType: "restaurant",
  // Defaults assume F&B until the real tenant config loads — mirrors the
  // backend's Tenant::getDefaultMenuFilterSettings() fallback for unknown types.
  menuFilterSettings: { veg_non_veg: true, gender: false, item_nature: false, age_group: false, brand: false, color: false },
  templateType: "template15",
  theme: { primaryColor: "#000000", secondaryColor: "#ffffff", accentColor: "#cccccc", fontFamily: "Poppins" },
  backgroundImages: { banner: "", newsletter: "", footer: "" },
  banner: {
    heading: "",
    sub_heading: "",
    description: "",
    button_text: "Order Now"
  },
  about: { heading: "", subHeading: "", paragraphs: [], mainImage: "", insetImage: "" },
  specialMenu: { heading: "Special Menu", subHeading: "Chef's Recommendation", items: [] },
  menu: { heading: "Our Categories", subHeading: "Explore our menu", categories: [] },
  gallery: { heading: "Our Gallery", description: "Visual highlights", items: [] },
  trustBadges: { items: [] },
  team: { heading: "Our Team", description: "The experts behind the magic", members: [] },
  blog: { heading: "Latest News", description: "Updates and stories", posts: [] },
  process: { heading: "How It Works", description: "Simple steps to your meal", steps: [] },
  newsletter: { heading: "Subscribe", description: "Get the latest updates" },
  testimonials: { heading: "Testimonials", description: "What our customers say", items: [] },
  footer: {
    about: {
      heading: "About Us",
      description: "",
      social: []
    },
    contact: { address: "", phone: "", email: "" },
    hours: { heading: "Opening Hours" },
    policies: [],
    whyChooseUs: { heading: "Why Choose Us", items: [] },
    newsletter: { heading: "Subscribe", description: "Stay updated with our latest offers" },
    copyright: "© 2026 All Rights Reserved."
  },
  currency: { symbol: "₹", position: 5, decimalPoints: 2 },
  orderSetup: {
    foodPreparationTime: 30,
    scheduleSlotDuration: 30,
    takeawayEnabled: true,
    deliveryEnabled: true,
    freeDeliveryKm: 2,
    basicDeliveryCharge: 0,
    chargePerKilo: 10
  },
  sections: [],
  navLinks: []
};

/**
 * Merges the API 'init' response into the config.
 */
export const mergeConfigWithBranding = (config, brandingData) => {
  if (!brandingData) return config;

  const settings = brandingData.settings || {};
  const restaurant = brandingData.restaurant || {};
  const branding = restaurant.branding || {};
  const content = brandingData.content || {};

  const cleanColor = (c) => (c ? c.replace(/[`'"]/g, '').trim() : null);

  // 1. Resolve Template
  const apiTemplate = brandingData.template || restaurant.template;
  const templateId = apiTemplate?.id || settings.template_id;
  const templateSlug = apiTemplate?.slug || settings.template_type || 'template15';

  // Try to use templateId (e.g. 1 -> template1), fallback to slug, default to template15
  const templateType = templateId ? `template${templateId}` : templateSlug;

  // Resolved ahead of menuFilterSettings below since the sensible default
  // for the latter depends on it.
  const outletType = settings.outlet_type || restaurant.outlet_type || config.outletType;

  // Veg/Non-Veg only makes sense for a classic restaurant/cafe menu — when
  // the backend hasn't explicitly configured menu_filter_settings for this
  // tenant, only default it on for outletType 'restaurant'. Previously this
  // fell back to the skeleton's F&B-shaped default regardless of outlet
  // type, so e.g. a dry-fruit-shop or sweet-shop tenant with no explicit
  // backend config still showed Veg/Non-Veg filters.
  const outletDefaultMenuFilterSettings = {
    veg_non_veg: outletType === 'restaurant',
    gender: false,
    item_nature: false,
    age_group: false,
    brand: false,
    color: false
  };

  const rawMenuFilterSettings = restaurant.menu_filter_settings || settings.menu_filter_settings || outletDefaultMenuFilterSettings;
  // Meat stores carry non-veg/meat products by definition — force filters off when outletType === 'meat_store'
  const menuFilterSettings = outletType === 'meat_store'
    ? { veg_non_veg: false, gender: false, item_nature: false, age_group: false, brand: false, color: false }
    : rawMenuFilterSettings;

  return {
    ...config,
    id: String(restaurant.id || config.id),
    name: settings.company_name || restaurant.name || config.name,
    logo: branding.primary_logo || brandingData.brand_assets?.primary_logo || config.logo,
    outletType,
    menuFilterSettings,
    templateType,

    theme: {
      primaryColor: cleanColor(branding.primary_color || brandingData.theme_colors?.primary) || config.theme.primaryColor,
      secondaryColor: cleanColor(branding.secondary_color || brandingData.theme_colors?.secondary) || config.theme.secondaryColor,
      accentColor: cleanColor(brandingData.theme_colors?.accent) || config.theme.accentColor,
      fontFamily: branding.font_family || settings.site_font_family || config.theme.fontFamily || "Poppins"
    },

    backgroundImages: {
      banner:
        branding.banner_image ||
        brandingData.brand_assets?.banner_image ||
        settings.banner_image ||
        content.banner?.image ||
        content.banner?.image_url ||
        content.banner?.banner_image ||
        content.banner?.background_image ||
        content.banner?.slides?.[0]?.image ||
        content.banner?.slides?.[0]?.url ||
        config.backgroundImages.banner,
      newsletter: branding.banner_image || content.banner?.slides?.[0]?.image || config.backgroundImages.newsletter,
      footer: brandingData.brand_assets?.footer_image || config.backgroundImages.footer
    },

    banner: {
      ...config.banner,
      heading: content.banner?.heading || settings.banner_heading || "Welcome",
      sub_heading: content.banner?.sub_heading || "",
      description: content.banner?.description || settings.banner_description || "",
      button_text: content.banner?.button_text || "Order Now",
      image:
        branding.banner_image ||
        brandingData.brand_assets?.banner_image ||
        settings.banner_image ||
        content.banner?.image ||
        content.banner?.image_url ||
        content.banner?.banner_image ||
        content.banner?.slides?.[0]?.image ||
        "",
      slides: content.banner?.slides || []
    },

    about: {
      heading: content.about?.heading || settings.about_us_heading || "Our Story",
      sub_heading: content.about?.sub_heading || "",
      paragraphs: content.about?.paragraphs || [],
      image: content.about?.main_image || config.about.mainImage,
      insetImage: content.about?.inset_image || config.about.insetImage
    },

    owner: {
      name: content.owner?.name || "",
      role: content.owner?.role || "",
      image: content.owner?.image || "",
      quote: content.owner?.quote || "",
      story: content.owner?.story || ""
    },

    specialMenu: {
      heading: content.special_menu?.heading || "Our Signature Specialties",
      sub_heading: content.special_menu?.sub_heading || content.special_menu?.description || ""
    },
    menu: {
      heading: content.menu?.heading || "Our Categories",
      sub_heading: content.menu?.sub_heading || "Explore our menu"
    },

    team: {
      heading: content.team?.heading || "Our Team",
      sub_heading: content.team?.sub_heading || "",
      members: content.team?.items || content.team?.members || []
    },

    blog: {
      heading: content.blog?.heading || "Latest Stories",
      sub_heading: content.blog?.sub_heading || "",
      posts: content.blog?.items || content.blog?.posts || []
    },

    process: {
      heading: content.process?.heading || "How It Works",
      sub_heading: content.process?.sub_heading || "",
      items: content.process?.items || content.process?.steps || []
    },

    testimonials: {
      heading: content.testimonials?.heading || config.testimonials.heading,
      sub_heading: content.testimonials?.sub_heading || config.testimonials.description,
      items: Array.isArray(content.testimonials?.items) ? content.testimonials.items : []
    },

    gallery: {
      ...config.gallery,
      heading: content.gallery?.heading || "Gallery",
      sub_heading: content.gallery?.sub_heading || content.gallery?.description || "",
      items: (Array.isArray(content.gallery?.items) ? content.gallery.items : []).map(item => ({
        ...item,
        image: item.image || item.imageSrc || ""
      }))
    },

    trustBadges: {
      items: Array.isArray(content.trust_badges?.items) ? content.trust_badges.items : (Array.isArray(content.trust_badges) ? content.trust_badges : [])
    },

    footer: {
      ...config.footer,
      about: {
        heading: settings.company_name || config.footer.about.heading,
        description: content.footer?.tagline || settings.about_us_content || config.footer.about.description,
        social: (() => {
          let socials = [];
          const s = content.footer?.social;
          if (s) {
            if (s.facebook && s.facebook.trim() !== '') socials.push({ icon: "fa fa-facebook", href: s.facebook });
            if (s.instagram && s.instagram.trim() !== '') socials.push({ icon: "fa fa-instagram", href: s.instagram });
            if (s.twitter && s.twitter.trim() !== '') socials.push({ icon: "fa fa-twitter", href: s.twitter });
            if (s.youtube && s.youtube.trim() !== '') socials.push({ icon: "fa fa-youtube", href: s.youtube });
            if (s.whatsapp && s.whatsapp.trim() !== '') {
              const waLink = s.whatsapp.startsWith('http') ? s.whatsapp : `https://wa.me/${s.whatsapp.replace(/[^0-9]/g, '')}`;
              socials.push({ icon: "fa fa-whatsapp", href: waLink });
            }
            if (s.linkedin && s.linkedin.trim() !== '') socials.push({ icon: "fa fa-linkedin", href: s.linkedin });
          }

          if (socials.length === 0) {
            if (settings.social_media_facebook && settings.social_media_facebook.trim() !== '') socials.push({ icon: "fa fa-facebook", href: settings.social_media_facebook });
            if (settings.social_media_instagram && settings.social_media_instagram.trim() !== '') socials.push({ icon: "fa fa-instagram", href: settings.social_media_instagram });
            if (settings.social_media_twitter && settings.social_media_twitter.trim() !== '') socials.push({ icon: "fa fa-twitter", href: settings.social_media_twitter });
            if (settings.social_media_youtube && settings.social_media_youtube.trim() !== '') socials.push({ icon: "fa fa-youtube", href: settings.social_media_youtube });
          }
          return socials.length > 0 ? socials : config.footer.about.social;
        })()
      },
      newsletter: {
        heading: content.footer?.newsletter_heading || config.footer.newsletter.heading,
        description: content.footer?.newsletter_description || config.footer.newsletter.description
      },
      whyChooseUs: {
        heading: (content.footer?.why_choose_us_heading && !content.footer.why_choose_us_heading.includes("Charminar"))
          ? content.footer.why_choose_us_heading
          : `Why ${settings.company_name || restaurant.name || 'Choose Us'}?`,
        items: content.footer?.why_choose_us_items || config.footer.whyChooseUs.items
      },
      copyright: settings.site_copyright ? `Copyright © ${settings.site_copyright} ${settings.company_name || restaurant.name || config.name}` : `Copyright © 2026 ${settings.company_name || restaurant.name || config.name}`,
      contact: {
        heading: content.map?.heading || "Our Address",
        sub_heading: content.map?.sub_heading || "Find Us",
        description: content.map?.description || "",
        address: settings.company_address || config.footer.contact.address,
        phone: settings.company_phone || config.footer.contact.phone,
        email: settings.company_email || config.footer.contact.email
      },
      hours: {
        heading: content.footer?.hours_heading || config.footer.hours.heading
      },
      policies: (brandingData.legal_pages || [])
        .map(p => ({
          name: p.title,
          path: `/${p.type.replace(/_/g, '-')}`,
          htmlContent: cleanHtmlContent(p.content)
        }))
        .filter(p => p.htmlContent && p.htmlContent.trim().length > 0)
    },

    currency: {
      symbol: settings.site_default_currency_symbol || '₹',
      position: settings.site_currency_position || 5,
      decimalPoints: parseInt(settings.site_digit_after_decimal_point || '2', 10)
    },

    otp: {
      digitLimit: parseInt(settings.otp_digit_limit || '4', 10),
      expireTime: parseInt(settings.otp_expire_time || '20', 10),
      type: settings.otp_type || '5'
    },

    orderSetup: {
      foodPreparationTime: parseInt(settings.order_setup_food_preparation_time || 30, 10),
      scheduleSlotDuration: parseInt(settings.order_setup_schedule_order_slot_duration || 30, 10),
      takeawayEnabled: settings.order_setup_takeaway === 5,
      deliveryEnabled: settings.order_setup_delivery === 5,
      freeDeliveryKm: parseFloat(settings.order_setup_free_delivery_kilometer || 2),
      basicDeliveryCharge: parseFloat(settings.order_setup_basic_delivery_charge || 0),
      chargePerKilo: parseFloat(settings.order_setup_charge_per_kilo || 10)
    },

    sections: buildSectionsFromTemplate(TEMPLATES[templateType.toUpperCase()]?.components?.defaultSections || _defaultTemplateSections),
    navLinks: buildNavLinksFromSections(TEMPLATES[templateType.toUpperCase()]?.components?.defaultSections || _defaultTemplateSections)
  };
};

/**
 * Merge homepage content from admin-configured website_content into the
 * restaurant config, overriding static text with dynamic CMS values.
 */
export const mergeContentIntoConfig = (config, content) => {
  if (!content || typeof content !== 'object') return config;

  const merged = { ...config };
  const str = (v) => (v && typeof v === 'string' ? v : undefined);
  const arr = (v) => (Array.isArray(v) ? v : undefined);

  if (content.banner) {
    const b = content.banner;
    const bannerImg =
      b.image ||
      b.image_url ||
      b.banner_image ||
      b.background_image ||
      (Array.isArray(b.slides) && b.slides[0]?.image) ||
      (Array.isArray(b.slides) && b.slides[0]?.url);

    merged.banner = {
      ...merged.banner,
      ...(str(b.heading) && { heading: b.heading, mainHeading: b.heading }),
      ...(str(b.sub_heading) && { sub_heading: b.sub_heading, subHeading: b.sub_heading }),
      ...(str(b.description) && { description: b.description }),
      ...(str(b.button_text) && { button_text: b.button_text, buttonText: b.button_text }),
      ...(str(b.button_link) && { button_link: b.button_link, buttonLink: b.button_link }),
      ...(bannerImg && { image: bannerImg, image_url: bannerImg }),
      ...(arr(b.slides) && b.slides.length > 0 && { slides: b.slides }),
    };

    if (bannerImg) {
      merged.backgroundImages = {
        ...merged.backgroundImages,
        banner: bannerImg
      };
    }
  }
  if (content.about) {
    const a = content.about;
    merged.about = { ...merged.about, ...(str(a.heading) && { heading: a.heading }), ...(str(a.sub_heading) && { subHeading: a.sub_heading }), ...(arr(a.paragraphs) && { paragraphs: a.paragraphs }), ...(str(a.main_image) && { mainImage: a.main_image }), ...(str(a.inset_image) && { insetImage: a.inset_image }) };
  }
  if (content.special_menu) {
    merged.specialMenu = { ...merged.specialMenu, ...(str(content.special_menu.heading) && { heading: content.special_menu.heading }), ...(str(content.special_menu.sub_heading) && { subHeading: content.special_menu.sub_heading }) };
  }
  if (content.menu) {
    merged.menu = { ...merged.menu, ...(str(content.menu.heading) && { heading: content.menu.heading }), ...(str(content.menu.sub_heading) && { subHeading: content.menu.sub_heading }) };
  }
  if (content.gallery) {
    merged.gallery = { ...merged.gallery, ...(str(content.gallery.heading) && { heading: content.gallery.heading }), ...(str(content.gallery.sub_heading) && { subHeading: content.gallery.sub_heading }), ...(arr(content.gallery.items) && { items: content.gallery.items }) };
  }
  if (content.team) {
    merged.team = { ...merged.team, ...(str(content.team.heading) && { heading: content.team.heading }), ...(str(content.team.sub_heading) && { subHeading: content.team.sub_heading }), ...(arr(content.team.items) && { members: content.team.items }) };
  }
  if (content.process) {
    merged.process = { ...merged.process, ...(str(content.process.heading) && { heading: content.process.heading }), ...(str(content.process.sub_heading) && { subHeading: content.process.sub_heading }), ...(arr(content.process.items) && { items: content.process.items }) };
  }
  if (content.testimonials) {
    merged.testimonials = { ...merged.testimonials, ...(str(content.testimonials.heading) && { heading: content.testimonials.heading }), ...(str(content.testimonials.sub_heading) && { subHeading: content.testimonials.sub_heading }), ...(arr(content.testimonials.items) && { items: content.testimonials.items }) };
  }
  if (content.blog) {
    merged.blog = { ...merged.blog, ...(str(content.blog.heading) && { heading: content.blog.heading }), ...(str(content.blog.sub_heading) && { subHeading: content.blog.sub_heading }), ...(arr(content.blog.items) ? { posts: content.blog.items } : arr(content.blog.posts) ? { posts: content.blog.posts } : {}) };
  }
  if (content.footer) {
    const f = content.footer;
    merged.footer = { ...merged.footer, ...(str(f.newsletter_heading) && { newsletterHeading: f.newsletter_heading }), ...(str(f.newsletter_description) && { newsletterDescription: f.newsletter_description }), ...(str(f.why_choose_us_heading) && { whyChooseUsHeading: f.why_choose_us_heading }), ...(arr(f.why_choose_us_items) && { whyChooseUsItems: f.why_choose_us_items }) };
  }
  if (content.map) {
    merged.map = { ...merged.map, ...(str(content.map.heading) && { heading: content.map.heading }), ...(str(content.map.sub_heading) && { subHeading: content.map.sub_heading }), ...(str(content.map.description) && { description: content.map.description }) };
  }
  if (content.trust_badges) {
    if (arr(content.trust_badges.items)) {
      merged.trustBadges = { ...merged.trustBadges, items: content.trust_badges.items };
    } else if (arr(content.trust_badges)) {
      merged.trustBadges = { ...merged.trustBadges, items: content.trust_badges };
    }
  }

  // Section visibility overrides
  const sectionMap = { banner: 'banner', about: 'about', special_menu: 'specialMenu', menu: 'menu', gallery: 'gallery', team: 'team', blog: 'blog', process: 'process', testimonials: 'testimonials', map: 'map', trust_badges: 'trustBadges' };
  const activeOverrides = {};
  Object.entries(sectionMap).forEach(([key, configKey]) => {
    if (content[key] && typeof content[key].enabled === 'boolean') activeOverrides[configKey] = content[key].enabled;
  });
  if (Object.keys(activeOverrides).length) merged.sectionActive = { ...(merged.sectionActive || {}), ...activeOverrides };

  return merged;
};

/**
 * INITIAL TEST STATE
 */
export const baseConfig = { ...skeletonConfig };

/**
 * SYNC config access (cached)
 * STRICT MOCK: Always uses the Master Sample Specification for testing.
 */
export const getRestaurantConfigSync = () => {
  return baseConfig;
};

/**
 * ASYNC config access (API)
 * Uses live branding data from brandingService if available,
 * falls back to the sample-based baseConfig otherwise.
 */
export const getRestaurantConfig = async () => {
  const liveData = brandingService.initData;
  if (liveData) {
    console.log("✅ Using LIVE init data for config");
    let config = mergeConfigWithBranding(skeletonConfig, liveData);
    if (liveData.content) config = mergeContentIntoConfig(config, liveData.content);
    return config;
  }
  console.log("⚠️ No live init data, falling back to sample config");
  return baseConfig;
};

export const restaurantConfig = getRestaurantConfigSync();
export const getBaseConfig = () => baseConfig;
export default restaurantConfig;